import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface ScaledScreenProps {
    width: number
    height: number
    className?: string
    children: ReactNode
}

// Renders its children at a fixed design size and scales them to fit, so the
// live mockups look identical on every screen width.
export const ScaledScreen: FC<ScaledScreenProps> = ({ width, height, className, children }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [scale, setScale] = useState(0)

    useEffect(() => {
        const node = ref.current

        if (!node) return

        const update = () => setScale(node.clientWidth / width)

        update()

        const observer = new ResizeObserver(update)

        observer.observe(node)

        return () => observer.disconnect()
    }, [width])

    return (
        <div ref={ref} className={cn('relative w-full overflow-hidden', className)} style={{ aspectRatio: `${width} / ${height}` }}>
            <div
                className='absolute left-0 top-0 origin-top-left transition-opacity duration-500'
                style={{ width, height, transform: `scale(${scale})`, opacity: scale ? 1 : 0 }}
            >
                {children}
            </div>
        </div>
    )
}

interface FrameProps {
    className?: string
    children: ReactNode
}

export const LaptopFrame: FC<FrameProps> = ({ className, children }): ReactNode => {
    return (
        <div className={cn('relative', className)}>
            <div className='relative rounded-[3.2%_/_5%] bg-[linear-gradient(160deg,#1F2633,#0B0E14_45%)] p-[1.15%] pb-[1.5%] shadow-[0_0_0_1px_rgba(255,255,255,0.09),0_50px_110px_-36px_rgba(2,6,23,0.9)]'>
                <div className='relative overflow-hidden rounded-[1.1%_/_1.8%] bg-slate-50 ring-1 ring-black/60'>
                    <ScaledScreen width={1280} height={800}>
                        {children}
                    </ScaledScreen>
                </div>

                <div aria-hidden='true' className='absolute left-1/2 top-[1.15%] flex h-[2.5%] w-[10%] -translate-x-1/2 items-center justify-center rounded-b-[30%] bg-[#0B0E14]'>
                    <span className='size-[18%] max-h-1.5 max-w-1.5 rounded-full bg-[#1d2a3a] ring-1 ring-sky-900/60' />
                </div>
            </div>

            <div aria-hidden='true' className='relative -mx-[6%] h-[clamp(10px,1.9vw,22px)] rounded-b-[40%_100%] bg-[linear-gradient(to_bottom,#E2E5EA,#A3A9B3_55%,#6E7480)] shadow-[0_26px_40px_-18px_rgba(15,23,42,0.55)]'>
                <span className='absolute left-1/2 top-0 h-[42%] w-[15%] -translate-x-1/2 rounded-b-[999px] bg-[linear-gradient(to_bottom,#9BA1AB,#C3C8D0)]' />
            </div>
        </div>
    )
}

export const PhoneFrame: FC<FrameProps> = ({ className, children }): ReactNode => {
    return (
        <div className={cn('relative', className)}>
            <div className='relative rounded-[17%_/_8%] bg-[linear-gradient(150deg,#2A3343,#0B0E14_40%)] p-[3.4%] shadow-[0_0_0_1px_rgba(255,255,255,0.12),0_40px_80px_-24px_rgba(2,6,23,0.85)]'>
                <div className='relative overflow-hidden rounded-[13.5%_/_6.3%] bg-white ring-1 ring-black/70'>
                    <ScaledScreen width={390} height={844}>
                        {children}
                    </ScaledScreen>

                    <div aria-hidden='true' className='absolute left-1/2 top-[1.5%] h-[3.7%] w-[31%] -translate-x-1/2 rounded-full bg-black' />
                </div>
            </div>

            <span aria-hidden='true' className='absolute -left-[1.6%] top-[18%] h-[6%] w-[1.8%] rounded-l-full bg-[#1F2633]' />
            <span aria-hidden='true' className='absolute -left-[1.6%] top-[27%] h-[10%] w-[1.8%] rounded-l-full bg-[#1F2633]' />
            <span aria-hidden='true' className='absolute -right-[1.6%] top-[24%] h-[13%] w-[1.8%] rounded-r-full bg-[#1F2633]' />
        </div>
    )
}
